/**
 * Canonical struct-role color palette — the SHARED single source of truth for
 * type-color coding across BOTH the PDF editor (`web/static/editor/`) and the
 * O365 a11y diff-view (`web/static/a11y-view/`).
 *
 * This module lives at `web/static/shared/` (the cross-tree shared tier,
 * mirroring `shared/editor-routing.ts`) because it now has two consumers, not
 * one (rule #11 / A.6 — extract on the second site; the a11y-view is that
 * second site). It emits as ESM via `web/static/tsconfig.esm-modules.emit.json`
 * and is served at `/static/shared/role-palette.js`.
 *
 * The PDF right-pane structure cards (`cards.ts` `ROLE_TREE`, rendered via the
 * `--col-*` custom properties declared in `editor.css`) and the left-pane
 * PDF-preview overlay boxes (`overlays.ts` `renderOverlays`) derive their
 * per-type color from `ROLE_CSS_VAR` / `boxColorForRole`. The a11y-view's
 * overlays + structure cards derive theirs from `paletteForRowKind` — which
 * folds a format-agnostic `TextViewRowKind` onto the SAME canonical roles — so
 * a heading is the same blue in the PDF editor and the O365 view (A.19).
 *
 * WHY the hex values are duplicated with editor.css: CSS custom properties
 * aren't readable from a static TS module, so `ROLE_HEX` below is a second
 * surface of the same palette as the `--col-*` declarations in editor.css
 * ("Editor-scoped overlay color tokens"). Keep both edits in the same
 * commit — the drift check is now `tools/lint/lint-palette-hex-css-drift.py`
 * (verifies every `ROLE_HEX` key ↔ its `--col-*` declaration stays
 * pixel-identical).
 *
 * @module role-palette
 */
/**
 * Canonical role token → CSS custom-property name declared in editor.css.
 *
 * `Title` is an O365-unique canonical role (a doc/slide title has no PDF
 * struct-role peer) — it carries its own `--col-title` token per the
 * editor-a11y-view-ux-260812 Phase-1b ruling (§G-Q1 (a): a distinct kind earns
 * a distinct color rather than folding onto `H1`).
 */
export const ROLE_CSS_VAR = Object.freeze({
    Title: "--col-title",
    H1: "--col-h1", H2: "--col-h2", H3: "--col-h3",
    P: "--col-p",
    L: "--col-list", LI: "--col-li",
    Figure: "--col-figure", Table: "--col-table", Caption: "--col-caption",
    Formula: "--col-formula", Span: "--col-span",
    Audio: "--col-audio", Video: "--col-video", Link: "--col-link",
    Note: "--col-note", Artifact: "--col-artifact",
});
/**
 * Canonical role token → hex color. MUST stay pixel-identical to the
 * `--col-*` declarations in editor.css (`:root` block). Used only where a
 * concrete hex is needed (computing a translucent-fill fallback for roles
 * with no CSS var); prefer `ROLE_CSS_VAR` (`var(--col-x)`) wherever the
 * browser can resolve the custom property directly, since that also picks
 * up the dark-mode / `prefers-contrast` overrides editor.css defines for
 * the same tokens.
 */
export const ROLE_HEX = Object.freeze({
    Title: "#3730a3",
    H1: "#1a56a0", H2: "#2563eb", H3: "#3b82f6",
    P: "#374151",
    L: "#0f766e", LI: "#10b981",
    Figure: "#7c3aed", Table: "#0e7490", Caption: "#92400e",
    Formula: "#c2410c", Span: "#6b7280",
    Audio: "#db2777", Video: "#a21caf", Link: "#0369a1",
    Note: "#9d174d", Artifact: "#9ca3af",
});
/**
 * Broader PDF struct-tree roles that don't get their own type-chip in the
 * right pane (heading depths beyond H3, table/list sub-elements, etc.) fold
 * onto the closest canonical role's color so the left-pane overlay is never
 * left uncolored. Mirrors the groupings `overlays.ts`'s older
 * `_DEFAULT_ROLE_TO_CATEGORY` used for its (still-live, separately
 * consumed) category palette.
 */
const ALIAS_TO_CANONICAL_ROLE = Object.freeze({
    H: "H1", H4: "H3", H5: "H3", H6: "H3",
    BlockQuote: "P", Quote: "P",
    Form: "Figure",
    TR: "Table", TH: "Table", TD: "Table",
    THead: "Table", TBody: "Table", TFoot: "Table",
    Lbl: "L", LBody: "L", TOC: "L", TOCI: "L",
    Reference: "Note", Annot: "Note",
});
/** Fallback color for roles with no canonical or aliased match (matches --col-artifact). */
export const FALLBACK_ROLE_HEX = "#9ca3af";
/** Resolve a struct role to its canonical `ROLE_HEX` / `ROLE_CSS_VAR` key, or null if unknown. */
export function canonicalRole(role) {
    if (!role)
        return null;
    const r = String(role);
    if (ROLE_HEX[r])
        return r;
    return ALIAS_TO_CANONICAL_ROLE[r] ?? null;
}
/** CSS custom-property reference name (e.g. `--col-h1`) for a role, or null if unknown. */
export function cssVarForRole(role) {
    const canon = canonicalRole(role);
    return canon ? ROLE_CSS_VAR[canon] : null;
}
/** Concrete hex color for a role. Always resolves — falls back to `FALLBACK_ROLE_HEX`. */
export function hexForRole(role) {
    const canon = canonicalRole(role);
    return canon ? ROLE_HEX[canon] : FALLBACK_ROLE_HEX;
}
/** Convert a `#rrggbb` hex color to `rgba(r, g, b, a)`. Unrecognized shapes pass through. */
export function hexToRgba(hex, alpha) {
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m)
        return hex;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/**
 * Compute the `{fill, border}` CSS values for a left-pane overlay box of
 * the given role. When the role resolves to a canonical `--col-*` token,
 * `border` references the CSS var directly (so it tracks theme /
 * high-contrast overrides) and `fill` is a `color-mix()` translucent tint
 * of the same var. Unknown roles fall back to a static neutral color.
 */
export function boxColorForRole(role) {
    const cssVar = cssVarForRole(role);
    if (cssVar) {
        return {
            fill: `color-mix(in srgb, var(${cssVar}) 14%, transparent)`,
            border: `var(${cssVar})`,
        };
    }
    return {
        fill: hexToRgba(FALLBACK_ROLE_HEX, 0.14),
        border: FALLBACK_ROLE_HEX,
    };
}
// ─── O365 kind → canonical role (cross-format same color) ─────────────────────
/**
 * Map a format-agnostic O365 `TextViewRowKind` (`view-types.ts`) onto the SAME
 * canonical struct-role bucket a PDF item resolves to, so an equivalent element
 * gets the SAME color cross-format (a `body` paragraph in the O365 view is the
 * same grey-P a PDF `P` uses). This is the cross-editor unification the shared
 * palette exists for (A.19). `heading` resolves to `Hn` by depth via the row's
 * `level` field (see {@link paletteForRowKind}); `title` is O365-unique and
 * keeps its own `Title`/`--col-title` bucket (Phase-1b §G-Q1 (a)).
 *
 * Closed map (→ A.5): every `TextViewRowKind` member has exactly one entry.
 * Kept as a plain string-keyed record (not typed on `TextViewRowKind`) so this
 * shared module carries NO import from the a11y-view tree — the a11y-view is
 * the consumer, not a dependency.
 */
export const KIND_TO_CANONICAL_ROLE = Object.freeze({
    title: "Title",
    heading: "H1", // refined to H2/H3 by row.level in paletteForRowKind
    body: "P",
    list: "L",
    "image-alt": "Figure",
    "table-cell": "Table",
    notes: "Note",
});
/**
 * Resolve an O365 row kind (+ optional heading `level`) to its palette color.
 * Composes {@link KIND_TO_CANONICAL_ROLE} with `cssVarForRole`/`hexForRole`.
 * For `heading`, `level` 1/2/3+ maps to `H1`/`H2`/`H3` (mirroring how the PDF
 * editor colors heading depths); an absent/other level defaults to `H1`. The
 * a11y-view calls this for BOTH the overlay box (border/fill) and the
 * structure-card accent so a heading is the same blue in both editors.
 */
export function paletteForRowKind(kind, level) {
    let role = KIND_TO_CANONICAL_ROLE[kind] ?? null;
    if (role === "H1" && kind === "heading" && typeof level === "number") {
        role = level <= 1 ? "H1" : level === 2 ? "H2" : "H3";
    }
    if (!role)
        return { cssVar: null, hex: FALLBACK_ROLE_HEX };
    return { cssVar: ROLE_CSS_VAR[role] ?? null, hex: ROLE_HEX[role] ?? FALLBACK_ROLE_HEX };
}
